/* eslint-disable simple-import-sort/imports */
import React, { useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { keyframes, styled } from 'styled-components';
import { z } from 'zod';

import { apiKey } from 'constants';
import {
  EMAIL_REQUIRED_ERROR,
  MESSAGE_REQUIRED_ERROR,
  NAME_REQUIRED_ERROR,
} from 'constants/errors';
import { trackContactFormLead } from 'utils/analytics';
import { createOpenAIEventId, trackOpenAILead } from 'utils/openaiPixel';

import { PrimaryButton } from '../components/Buttons';
import { Reveal } from '../components/Motion';
import { CALENDLY_URL, Container, Section, v2 } from '../v2';

const schema = z.object({
  name: z.string().min(1, { message: NAME_REQUIRED_ERROR }),
  email: z.string().email({ message: EMAIL_REQUIRED_ERROR }),
  message: z.string().min(1, { message: MESSAGE_REQUIRED_ERROR }),
});

const Wrapper = styled(Section)`
  background: ${v2.bgSoft};
  border-top: 1px solid ${v2.border};
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Panel = styled.div`
  position: relative;
  border-radius: 32px;
  overflow: hidden;
  padding: clamp(40px, 6vw, 80px);
  border: 1px solid ${v2.borderStrong};
  background: ${v2.bgElevated};
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -60%;
    background: conic-gradient(
      from 0deg,
      rgba(30, 150, 252, 0.32),
      rgba(255, 159, 79, 0.26),
      rgba(30, 150, 252, 0.05),
      rgba(30, 150, 252, 0.32)
    );
    animation: ${rotate} 18s linear infinite;
    z-index: -2;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1.5px;
    border-radius: 31px;
    background: radial-gradient(
        90% 110% at 50% 0%,
        rgba(30, 150, 252, 0.14),
        transparent 60%
      ),
      ${v2.bgElevated};
    z-index: -1;
  }
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: clamp(40px, 5vw, 72px);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Heading = styled.h2`
  font-family: ${v2.fontDisplay};
  font-size: clamp(2rem, 4.2vw, 3.2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.08;
  color: ${v2.text};
  margin: 0 0 20px;

  .grad {
    background: ${v2.gradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SubText = styled.p`
  font-family: ${v2.fontBody};
  font-size: clamp(1rem, 1.4vw, 1.12rem);
  line-height: 1.65;
  color: ${v2.textMuted};
  margin: 0 0 32px;
  max-width: 460px;
`;

const ContactInfo = styled.p`
  margin: 26px 0 0;
  font-family: ${v2.fontBody};
  font-size: 14px;
  color: ${v2.textFaint};

  a {
    color: ${v2.textMuted};
    font-weight: 600;
    border-bottom: 1px solid ${v2.border};
    transition:
      color 0.25s ease,
      border-color 0.25s ease;

    &:hover {
      color: ${v2.text};
      border-color: ${v2.text};
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const inputStyles = `
  width: 100%;
  padding: 15px 18px;
  border-radius: 14px;
  border: 1px solid ${v2.borderStrong};
  background: rgba(255, 255, 255, 0.05);
  color: ${v2.text};
  font-family: ${v2.fontBody};
  font-size: 15px;
  line-height: 1.5;
  transition: border-color 0.25s ease, background 0.25s ease,
    box-shadow 0.25s ease;

  &::placeholder {
    color: ${v2.textFaint};
  }

  &:focus {
    border-color: rgba(30, 150, 252, 0.65);
    background: rgba(30, 150, 252, 0.07);
    box-shadow: 0 0 0 4px rgba(30, 150, 252, 0.14);
  }

  &:disabled {
    opacity: 0.55;
  }
`;

const TextInput = styled.input`
  ${inputStyles}
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 132px;
  resize: vertical;
`;

const ErrorText = styled.span`
  font-family: ${v2.fontBody};
  font-size: 13px;
  color: #ff6a66;
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 6px;

  span {
    font-family: ${v2.fontBody};
    font-size: 14px;
    color: ${v2.textFaint};
  }

  a.calendly {
    font-family: ${v2.fontBody};
    font-size: 14.5px;
    font-weight: 700;
    color: #5bb8ff;
    transition: color 0.25s ease;

    &:hover {
      color: ${v2.text};
    }
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 100px;
  font-family: ${v2.fontBody};
  font-size: 15.5px;
  font-weight: 700;
  color: #fff;
  background: ${v2.gradientBtn};
  box-shadow:
    0 8px 32px rgba(30, 150, 252, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  transition:
    box-shadow 0.3s ease,
    transform 0.2s ease,
    opacity 0.3s ease;

  &:hover {
    box-shadow:
      0 12px 48px rgba(30, 150, 252, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.55;
    cursor: wait;
  }
`;

const SuccessBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 18px;
  padding: 40px 24px;
  border-radius: 18px;
  border: 1px solid rgba(52, 211, 153, 0.4);
  background: rgba(52, 211, 153, 0.08);

  svg {
    width: 52px;
    height: 52px;
  }

  p {
    margin: 0;
    font-family: ${v2.fontBody};
    font-size: 16.5px;
    font-weight: 600;
    line-height: 1.6;
    color: ${v2.text};
    max-width: 360px;
  }
`;

const ContactV2 = () => {
  // One conversion ID per form instance: OpenAI deduplicates on it, so this
  // form can never be counted as more than one lead.
  const leadEventId = useRef(createOpenAIEventId());
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm({
    defaultValues: { name: '', email: '', message: '' },
    resolver: zodResolver(schema),
    reValidateMode: 'onBlur',
  });

  const onSubmit = async (data) => {
    const response = await fetch(apiKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      trackOpenAILead(leadEventId.current);
      trackContactFormLead();
    }
  };

  return (
    <Wrapper id="kontakt">
      <Container>
        <Reveal>
          <Panel>
            <PanelGrid>
              <div>
                <Heading>
                  Endlich <span className="grad">automatisierte</span>{' '}
                  Abrechnung!
                </Heading>
                <SubText>
                  Von Zahnärzten für Zahnärzte entwickelt. Schreib uns eine
                  Nachricht oder buche direkt einen Termin.
                </SubText>
                <PrimaryButton
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Termin vereinbaren
                </PrimaryButton>
                <ContactInfo>
                  Oder direkt per E-Mail:{' '}
                  <a href="mailto:info@foxi-abrechnung.de">
                    info@foxi-abrechnung.de
                  </a>
                </ContactInfo>
              </div>

              {isSubmitSuccessful ? (
                <SuccessBox>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle
                      cx="12"
                      cy="12"
                      r="11"
                      fill="rgba(52, 211, 153, 0.18)"
                    />
                    <path
                      d="m7 12.4 3.2 3.2L17 8.8"
                      stroke="#34D399"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p>
                    Deine Nachricht wurde erfolgreich an das FOXI Team
                    geschickt!
                  </p>
                </SuccessBox>
              ) : (
                <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <FieldRow>
                    <Field>
                      <TextInput
                        type="text"
                        placeholder="Name eingeben"
                        disabled={isSubmitting}
                        {...register('name')}
                      />
                      {errors.name && (
                        <ErrorText>{errors.name.message}</ErrorText>
                      )}
                    </Field>
                    <Field>
                      <TextInput
                        type="email"
                        placeholder="Email-Adresse eingeben"
                        disabled={isSubmitting}
                        {...register('email')}
                      />
                      {errors.email && (
                        <ErrorText>{errors.email.message}</ErrorText>
                      )}
                    </Field>
                  </FieldRow>
                  <Field>
                    <TextArea
                      placeholder="Ihre Nachricht"
                      disabled={isSubmitting}
                      {...register('message')}
                    />
                    {errors.message && (
                      <ErrorText>{errors.message.message}</ErrorText>
                    )}
                  </Field>
                  <SubmitRow>
                    <SubmitButton type="submit" disabled={isSubmitting}>
                      Nachricht schicken
                    </SubmitButton>
                    <span>oder</span>
                    <a
                      className="calendly"
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Termin vereinbaren
                    </a>
                  </SubmitRow>
                </Form>
              )}
            </PanelGrid>
          </Panel>
        </Reveal>
      </Container>
    </Wrapper>
  );
};

export default ContactV2;
